import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { BackendError, type BackendClient, type Session } from '@lumen/data';
import type { Platform } from '@lumen/platform';
import { Button } from '@lumen/ui';
import { Logo } from './Logo';
import { t } from '@lumen/platform';

export function AuthScreen({
  mode,
  platform,
  backend,
  onSession,
  initialEmail,
  inviteToken,
  onSwitchMode,
  title,
  subtitle,
}: {
  mode: 'login' | 'signup';
  platform: Platform;
  backend: BackendClient;
  onSession: (s: Session) => void;
  initialEmail?: string | null;
  /** On the join screen: sign-up joins the invite link's workspace; switching modes stays on the screen. */
  inviteToken?: string;
  onSwitchMode?: () => void;
  title?: string;
  subtitle?: string;
}) {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signupPolicy, setSignupPolicy] = useState<{ signup: string; domains?: string[] } | null>(null);

  useEffect(() => {
    if (mode !== 'signup') return;
    let alive = true;
    backend
      .request<{ signup: string; domains?: string[] }>(null, 'GET', '/api/auth/config')
      .then((c) => alive && setSignupPolicy(c))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [backend, mode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = mode === 'signup' ? await backend.signUp({ email, password, name, ...(inviteToken ? { inviteToken } : {}) }) : await backend.signIn({ email, password });
      onSession(s);
    } catch (err) {
      setError(err instanceof BackendError ? err.message : t('Something went wrong. Check your connection and try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lm-auth" data-testid="auth-screen">
      <header className="lm-auth-header">
        <Logo size={28} />
        <span className="lm-auth-brand">{t('Lumen')}</span>
      </header>
      <main className="lm-auth-main">
        <h1 className="lm-auth-title">{title ?? (mode === 'signup' ? t('Create your account') : t('Log in'))}</h1>
        <p className="lm-auth-subtitle">{subtitle ?? (mode === 'signup' ? t('Your ideas, docs and projects. Together.') : t('Welcome back to your workspace.'))}</p>
        {mode === 'signup' && !inviteToken && signupPolicy && signupPolicy.signup !== 'open' && (
          <p className="lm-auth-note" data-testid="auth-signup-policy">
            {signupPolicy.signup === 'domains'
              ? t('Sign-up is open to {domains} addresses, and to people who were invited.', { domains: signupPolicy.domains?.map((d) => `@${d}`).join(', ') ?? '' })
              : t('Sign-up is by invitation only. Use the email address you were invited with.')}
          </p>
        )}
        <form className="lm-auth-form" onSubmit={submit}>
          {mode === 'signup' && (
            <label className="lm-auth-label">
              {t('Name')}
              <input className="lm-input lm-auth-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder={t('Enter your name')} data-testid="auth-name" />
            </label>
          )}
          <label className="lm-auth-label">
            {t('Email')}
            <input
              className="lm-input lm-auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              // Return in the email field moves on to the password while that is still empty (P-801).
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || password) return;
                e.preventDefault();
                (e.currentTarget.form?.querySelector('input[type="password"]') as HTMLInputElement | null)?.focus();
              }}
              autoComplete="email"
              placeholder={t('Enter your email address…')}
              required
              data-testid="auth-email"
            />
          </label>
          <label className="lm-auth-label">
            {t('Password')}
            <input
              className="lm-input lm-auth-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              placeholder={t('Enter your password…')}
              required
              data-testid="auth-password"
            />
          </label>
          {error && (
            <div className="lm-auth-error" role="alert" data-testid="auth-error">
              {error}
            </div>
          )}
          <Button variant="primary" size="lg" block type="submit" disabled={busy} data-testid="auth-submit">
            {mode === 'signup' ? t('Continue') : t('Log in')}
          </Button>
        </form>
        {mode === 'login' && (
          <p className="lm-auth-switch">
            <a
              href="/forgot"
              onClick={(e) => {
                e.preventDefault();
                platform.navigation.push('/forgot');
              }}
              data-testid="auth-forgot"
            >
              {t('Forgot password?')}
            </a>
          </p>
        )}
        <p className="lm-auth-switch">
          {mode === 'signup' ? t('Already have an account? ') : t('New to Lumen? ')}
          <a
            href={mode === 'signup' ? '/login' : '/signup'}
            onClick={(e) => {
              e.preventDefault();
              setError(null);
              if (onSwitchMode) onSwitchMode();
              else platform.navigation.push(mode === 'signup' ? '/login' : '/signup');
            }}
            data-testid="auth-switch"
          >
            {mode === 'signup' ? t('Log in') : t('Create an account')}
          </a>
        </p>
      </main>
    </div>
  );
}

function AuthFrame({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="lm-auth" data-testid="auth-screen">
      <header className="lm-auth-header">
        <Logo size={28} />
        <span className="lm-auth-brand">{t('Lumen')}</span>
      </header>
      <main className="lm-auth-main">
        <h1 className="lm-auth-title">{title}</h1>
        {subtitle && <p className="lm-auth-subtitle">{subtitle}</p>}
        {children}
      </main>
    </div>
  );
}

const message = (err: unknown) => (err instanceof BackendError ? err.message : t('Something went wrong. Check your connection and try again.'));

function BackToLogin({ platform }: { platform: Platform }) {
  return (
    <p className="lm-auth-switch">
      <a
        href="/login"
        onClick={(e) => {
          e.preventDefault();
          platform.navigation.push('/login');
        }}
        data-testid="auth-back"
      >
        {t('Back to log in')}
      </a>
    </p>
  );
}

/** "Forgot password?": mails a reset link. */
export function ForgotPasswordScreen({ platform, backend }: { platform: Platform; backend: BackendClient }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'no-mail'>('idle');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('busy');
    setError(null);
    try {
      const r = await backend.request<{ mail: boolean }>(null, 'POST', '/api/auth/forgot', { email });
      setState(r.mail ? 'sent' : 'no-mail');
    } catch (err) {
      setError(message(err));
      setState('idle');
    }
  };
  return (
    <AuthFrame title={t('Reset your password')} subtitle={t('We\'ll email you a link to choose a new one.')}>
      {state === 'sent' ? (
        <p className="lm-auth-note" data-testid="forgot-sent">
          {t('If an account exists for')} {email}{t(', a reset link is on its way. Check your inbox.')}
        </p>
      ) : state === 'no-mail' ? (
        <p className="lm-auth-note" data-testid="forgot-no-mail">
          {t('This server can\'t send email yet. Ask the person who runs your Lumen to reset your password.')}
        </p>
      ) : (
        <form className="lm-auth-form" onSubmit={submit}>
          <label className="lm-auth-label">
            {t('Email')}
            <input className="lm-input lm-auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder={t('Enter your email address…')} required data-testid="forgot-email" />
          </label>
          {error && (
            <div className="lm-auth-error" role="alert" data-testid="auth-error">
              {error}
            </div>
          )}
          <Button variant="primary" size="lg" block type="submit" disabled={state === 'busy'} data-testid="forgot-submit">
            {t('Send reset link')}
          </Button>
        </form>
      )}
      <BackToLogin platform={platform} />
    </AuthFrame>
  );
}

/** The mailed reset link: choose a new password, then you're signed in. */
export function ResetPasswordScreen({ token, platform, backend, onSession }: { token: string; platform: Platform; backend: BackendClient; onSession: (s: Session) => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSession(await backend.request<Session>(null, 'POST', '/api/auth/reset', { token, password }));
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame title={t('Choose a new password')}>
      <form className="lm-auth-form" onSubmit={submit}>
        <label className="lm-auth-label">
          {t('New password')}
          <input className="lm-input lm-auth-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder={t('At least 8 characters')} required data-testid="reset-password" />
        </label>
        {error && (
          <div className="lm-auth-error" role="alert" data-testid="auth-error">
            {error}
          </div>
        )}
        <Button variant="primary" size="lg" block type="submit" disabled={busy} data-testid="reset-submit">
          {t('Set password and log in')}
        </Button>
      </form>
      <BackToLogin platform={platform} />
    </AuthFrame>
  );
}

/** The mailed verification link. Works signed in or out. */
export function VerifyEmailScreen({ token, platform, backend, onDone }: { token: string; platform: Platform; backend: BackendClient; onDone: () => void }) {
  const [state, setState] = useState<{ ok: true; email: string } | { ok: false; error: string } | null>(null);
  useEffect(() => {
    let alive = true;
    backend
      .request<{ email: string }>(null, 'POST', '/api/auth/verify', { token })
      .then((r) => alive && setState({ ok: true, email: r.email }))
      .catch((err) => alive && setState({ ok: false, error: message(err) }));
    return () => {
      alive = false;
    };
  }, [backend, token]);
  return (
    <AuthFrame title={state?.ok ? t('Email verified') : state ? t('Could not verify') : t('Verifying…')}>
      {state && (
        <p className="lm-auth-note" data-testid={state.ok ? 'verify-ok' : 'verify-error'}>
          {state.ok ? t('{email} is confirmed. Thanks!', { email: state.email }) : state.error}
        </p>
      )}
      {state && (
        <Button variant="primary" size="lg" block onClick={onDone} data-testid="verify-continue">
          {t('Continue to Lumen')}
        </Button>
      )}
      {!state && <span />}
      {state && !state.ok && <BackToLogin platform={platform} />}
    </AuthFrame>
  );
}

/** `/join/<token>`: an invite link. Signed out, sign up (or log in) and join; signed in, join with one click. */
export function JoinScreen({ token, platform, backend, session, onSession, onJoined }: { token: string; platform: Platform; backend: BackendClient; session: Session | null; onSession: (s: Session) => void; onJoined: (slug: string) => void }) {
  const [info, setInfo] = useState<{ workspace: { name: string; icon: string | null; slug: string }; inviter: string | null } | { error: string } | null>(null);
  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    backend
      .request<{ workspace: { name: string; icon: string | null; slug: string }; inviter: string | null }>(null, 'GET', `/api/join/${token}`)
      .then((r) => alive && setInfo(r))
      .catch((e) => alive && setInfo({ error: message(e) }));
    return () => {
      alive = false;
    };
  }, [backend, token]);
  const join = async (s: Session) => {
    setBusy(true);
    setError(null);
    try {
      const { slug } = await backend.request<{ slug: string }>(s.token, 'POST', `/api/join/${token}`, {});
      onSession(await backend.me(s.token));
      onJoined(slug);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  if (!info) return <div className="lm-loading" />;
  if ('error' in info) {
    return (
      <AuthFrame title={t('Invite link not valid')}>
        <p className="lm-auth-note" data-testid="join-invalid">
          {info.error}
        </p>
        <BackToLogin platform={platform} />
      </AuthFrame>
    );
  }
  const subtitle = t('{p0} invited you to {name} on Lumen.', { p0: info.inviter ?? 'Someone', name: info.workspace.name });
  if (session) {
    return (
      <AuthFrame title={t('Join {name}', { name: info.workspace.name })} subtitle={subtitle}>
        {error && (
          <div className="lm-auth-error" role="alert" data-testid="auth-error">
            {error}
          </div>
        )}
        <Button variant="primary" size="lg" block disabled={busy} onClick={() => void join(session)} data-testid="join-submit">
          {t('Join workspace')}
        </Button>
      </AuthFrame>
    );
  }
  return (
    <AuthScreen
      mode={mode}
      platform={platform}
      backend={backend}
      inviteToken={token}
      title={t('Join {name}', { name: info.workspace.name })}
      subtitle={subtitle}
      onSwitchMode={() => setMode(mode === 'signup' ? 'login' : 'signup')}
      // Sign-up joins on the server; logging in still needs the join call.
      onSession={(s) => (mode === 'signup' ? (onSession(s), onJoined(info.workspace.slug)) : void join(s))}
    />
  );
}
